import React, { useEffect, useCallback } from 'react'
import { Panel, TabPanel, Container, TyanDatetimeField } from '@sencha/ext-modern'

import Block from './Block'
import { useInstData } from 'tyan/public/hooks'
import Controls from './Controls'
import { FormDataContext, IsDialogContext } from 'tyan/public/components/context'
import { keyDown } from '@syncfusion/ej2-react-richtexteditor'
const edges = {
	left: 'east',
	right: 'west',
	top: 'south',
	bottom: 'north',
}

const CANCELBTNS = ['取消', 'CANCEL', '关闭', 'CLOSE']
Object.freeze(CANCELBTNS)

const renderOneFieldset = ({ title: name, _fieldset: fieldset, data, minWidth, defaultProps, fieldsetID }) => {
	return fieldset && fieldset.length ? <Block {...{ name, data, minWidth, fieldset, fieldsetID }} {...defaultProps} /> : null
}

const renderSubFieldsets = ({ _fieldsets: fieldsets, data, minWidth, defaultProps: { default: _default = {}, ...rest }, fieldsetID }) => {
	const hasGrid = JSON.stringify(fieldsets).includes('grid":')
	return <TabPanel id={`subfieldsets-${fieldsetID}`} flex={1} height="100%"
		tabBar={{ defaultTabUI: 'lightgrey', shadow: false, style: { backgroundColor: '#f5f5f5' }, layout: { pack: 'left' } }}
		{...(hasGrid ? { layout: { animation: '' } } : {})}
	>
		{
			fieldsets.map(({ _fieldset, key, cdt, title, badgeText, default: _myDefault = {}, ...myRest }, index) => {
				key = key || `${title}_${index}`
				return (!cdt || cdt()) && <Container /* itemId={`subfieldset-${index}-${fieldsetID}`}  */ key={key} scrollable
					layout="fit" allowHeader={undefined}
					tab={{
						flex: 0, minWidth: 100, title, badgeText, ui: "title", margin: '0',
						style: { fontSize: '16px', /* lineHeight: '14px',  */fontFamily: '微软雅黑' }
					}}
				>
					{
						renderOneFieldset({
							title, _fieldset, data, minWidth,
							defaultProps: { default: { ..._default, ..._myDefault }, ...rest, ...myRest },
							fieldsetID: `subfieldset-${key}-${fieldsetID}`
						})
					}
				</Container> || null
			}).filter(o => o)
		}
	</TabPanel>
}

const renderFieldset = ({ _fieldset, title, _fieldsets, data, minWidth = Ext.os.is.Phone ? 200 : 500, ...defaultProps }, fieldsetID) => {
	// minWidth: 控制fieldset的最小宽度
	if (_fieldset) return renderOneFieldset({ title, _fieldset, data, minWidth, defaultProps, fieldsetID })
	if (_fieldsets) return renderSubFieldsets({ _fieldsets, data, minWidth, defaultProps, fieldsetID })
	return null
}

const Form = props => {
	const { me, me: { formID } } = useInstData(props, useCallback(() => {
		// Tyan.checkForm.init(props.data)
		return {
			formID: `form-${Tyan.getUuid()}`,
			hasChangedValues: () => Tyan.pubForm.hasChangedValues(me.props.data)   // 检查有没有changedValues
		}
	}, []))

	const { isTooltip } = props

	/**
 * 键盘事件处理 
 * 1、ESC 按钮退出(移除，改到Form中去 2024/10/18)
 * 2、ENTER 按钮跳转到下一个可输入组件中去，如果下一个组件不在Form中，则跳转到第一个可操作组件
 * @param {*} param0 
 * @returns 
 */
	const handleKeyDown = useCallback(({ event: e }) => {
		const doESC = () => {
			let btn = null
			for (const el of Array.from(me.container.el.dom.getElementsByClassName('x-button'))) {
				/* 判断是否取消按钮 */
				if (!CANCELBTNS.includes(el.innerText)) continue

				/* 判断是否隐藏或无效 */
				let parent = el.parentElement
				while (parent) {
					if (parent.className.includes(' x-hidden') || parent.className.includes(' x-disabled'))
						break
					parent = parent.parentElement
				}
				!parent && (btn = el)
			}

			if (btn) {
				e.stopPropagation()
				e.stopImmediatePropagation()
				e.cancelBubble = true
				btn.click()
				return false
			}
			return true
		}

		// 处理回车
		const doENTER = () => {
			if (e.srcElement.tagName != 'INPUT')
				return true

			e.stopPropagation()
			e.stopImmediatePropagation()
			e.cancelBubble = true

			const focusable = Tyan.checkForm.getFocusable(me.container.bodyElement.dom)
			if (focusable.length > 1) {
				const currentIndex = focusable.indexOf(e.srcElement)
				const nextIndex = currentIndex === focusable.length - 1 ? 0 : currentIndex + 1
				setTimeout(() => focusable[nextIndex]?.focus())
			}
			return false
		}

		if (e.repeat || e.shiftKey || e.metaKey) return true

		if (!e.ctrlKey && !e.altKey && e.keyCode === Ext.event.Event.ESC) return doESC()
		if (!e.ctrlKey && !e.altKey && e.keyCode === Ext.event.Event.ENTER) return doENTER()

		return true
	}, [])

	useEffect(() => {
		Tyan.locationControl.forms.push(me)
		// 焦点置于当前Form
		setTimeout(() => {
			if (me?.container?.el?.contains(document.activeElement))
				return
			document.activeElement.blur()
			Tyan.checkForm.focus(me.formID)
		})

		return () => {
			const { forms } = Tyan.locationControl
			forms.includes(me) && forms.splice(forms.indexOf(me), 1)
		}
	}, [])

	const handleResizeDragStart = useCallback(() => {
		me.container.element.dom.classList.add('t-resizing')
		// console.log('handleResizeDragStart')
	}, [])

	const handleResizeDragEnd = useCallback(() => {
		me.container.element.dom.classList.remove('t-resizing')
		// console.log('handleResizeDragEnd')
	}, [])

	const { fieldsets, controls, data, maxWidth, minWidth, isManagerInner, isDialog, fullForm, shadow = true, ...formRest } = props
	return <FormDataContext.Provider value={data}>
		<IsDialogContext.Provider value={isDialog}>
			{!isTooltip && <Controls {...{ controls }} /> || null}
			<Container id={`container-${formID}`}
				ref={ref => ref && (window.container = me.container = ref.cmp.parent)}
				listeners={{ keyDown: { element: 'bodyElement', fn: handleKeyDown } }}
				layout={'vbox'/* fieldsets.length == 1 ? 'vbox' : 'fit' */} maxWidth={maxWidth} minWidth={minWidth} hidden={props.hidden}
			>
				<Panel id={`panel-container-${formID}`}
					{...(fieldsets.length === 1 ? { maxHeight: "calc(100% - 15px)" } : { flex: 1 })} {...(fullForm ? { flex: 1 } : {})}
					shadow={!isDialog && !isManagerInner && !isTooltip && !Tyan.isPhone && shadow} layout="fit" margin={isManagerInner || Tyan.isPhone ? '0' : "5 10 10 10"}
				>
					{fieldsets.map(({ title, key, cdt, docked, width, height, titleAlign, resizable, ...rest }, fieldsetIndex) => {
						const props = { title: title || null, docked, width, height, titleAlign, resizable }
						if (cdt && !cdt() || (!rest._fieldset && !rest._fieldsets)) return null

						const { name } = rest
						// collapsible={docked} Ext.React有bug，效果不好
						if (props.docked && props.resizable === undefined) {
							props.resizable = { split: true, edges: edges[docked], dynamic: true };
							props.onResizeDragStart = handleResizeDragStart
							props.onResizeDragEnd = handleResizeDragEnd
							props.onResizeDragCancel = handleResizeDragEnd
						}

						for (let key in props)
							props[key] === undefined && delete props[key]

						// width/height: 控制布局的默认宽度/高度，可以使用百分百或数字，如：30%、300px
						key = key || `${title}_${name}_${fieldsetIndex}`
						const fieldsetID = `fieldset-${Tyan.shortMd5(key)}-${formID}`
						return <Panel id={`panel-${fieldsetID}`} layout='fit' key={key} /* title={title || null}  */
							scrollable ui="light" {...props} onDrag
						>
							{renderFieldset({ ...formRest, ...rest, title, data }, fieldsetID)}
						</Panel>
					}).filter(o => o)}
				</Panel>
			</Container >
			{false && <TyanDatetimeField />}
		</IsDialogContext.Provider>
	</FormDataContext.Provider>
}

export default Form